import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Loader2, X, Eye, Clock, Camera } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// Aufsichts-Cockpit: Der Dozent sieht links die Prüfung live mit (Antwortstand,
// aktualisiert alle 5s) und rechts das Kamerabild des Prüflings + Sprech-/Chat-Steuerung.
export const ExamLiveView = ({ attemptId, getAuthHeader, onClose, videoStream, snapshotUrl, onControlsSlot }) => {
  const [data, setData] = useState(null);
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current && videoStream) {
      videoRef.current.srcObject = videoStream;
      videoRef.current.play().catch(() => {});
    }
  }, [videoStream]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/admin/exam-attempts/${attemptId}/live-answers`, getAuthHeader());
        if (alive) setData(r.data);
      } catch { /* noop */ }
    };
    load();
    const t = setInterval(load, 5000);
    return () => { alive = false; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptId]);

  const answered = data
    ? (data.questions || []).filter((q) => data.answers?.[q.id] !== undefined && String(data.answers[q.id]).trim() !== '').length
    : 0;
  const mins = data ? Math.floor((data.remaining_seconds || 0) / 60) : 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-2 sm:px-4 py-4" role="dialog" aria-modal="true">
      <div className="bg-cream-50 rounded-2xl max-w-6xl w-full max-h-[94vh] flex flex-col" data-testid="exam-live-view">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-cream-300">
          <div>
            <p className="font-mono text-[9.5px] uppercase font-semibold tracking-[0.22em] text-terra flex items-center gap-1.5">
              <Eye className="w-3.5 h-3.5" /> Aufsichts-Cockpit — Live-Mitlesen
            </p>
            <h3 className="font-display text-xl text-espresso mt-0.5">{data?.user_name || '…'}</h3>
            {data && (
              <p className="text-[12.5px] text-sand-600 mt-0.5">
                <Clock className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                Restzeit ~{mins} Min. · beantwortet {answered}/{(data.questions || []).length} · Stand wird alle 5 Sek. aktualisiert
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-2 rounded-lg border border-cream-400 text-sand-700 hover:bg-cream-200" data-testid="live-view-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 flex flex-col lg:flex-row min-h-0">
          {/* Rechte Spalte (auf Mobil oben): Video + Sprechen/Chat */}
          <div className="lg:order-2 lg:w-[320px] shrink-0 p-4 lg:border-l border-b lg:border-b-0 border-cream-300 space-y-3 overflow-y-auto">
            <div className="aspect-[4/3] bg-espresso rounded-xl overflow-hidden relative" data-testid="live-view-video-box">
              {videoStream ? (
                <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="live-view-video" />
              ) : snapshotUrl ? (
                <img src={snapshotUrl} alt="Kamerabild" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-cream-50/50 text-[11.5px] gap-2 px-4 text-center">
                  <Camera className="w-6 h-6" />
                  Noch kein Live-Video — unten „Video &amp; Sprechen" starten
                </div>
              )}
              <span className="absolute top-2 left-2 flex items-center gap-1.5 bg-black/60 text-cream-50 text-[9.5px] font-bold uppercase tracking-wider px-2 py-1 rounded-full">
                <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${videoStream ? 'bg-green-400' : 'bg-red-500'}`} /> {videoStream ? 'Live-Video' : 'Standbild'}
              </span>
            </div>
            {/* Slot: Chat + Sprechen-Steuerung wird per Portal hier eingeblendet */}
            <div ref={onControlsSlot} data-testid="live-view-controls-slot" />
            <p className="text-[11px] text-sand-500 leading-relaxed">
              Über „Video &amp; Sprechen" siehst und hörst du den Prüfling live — er hört dich ebenfalls. Der Chat bleibt parallel nutzbar.
            </p>
          </div>
          {/* Linke Spalte: Prüfung live mitlesen */}
          <div className="lg:order-1 flex-1 overflow-y-auto p-5 space-y-3 min-h-[200px]">
            {!data && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-terra" /></div>}
            {data && data.status !== 'laufend' && (
              <p className="text-[13.5px] text-sand-600 italic">Die Prüfung läuft nicht mehr (Status: {data.status}).</p>
            )}
            {data && (data.questions || []).map((q, i) => {
              const ans = data.answers?.[q.id];
              const hasAns = Array.isArray(ans) ? ans.length > 0 : ans !== undefined && String(ans).trim() !== '';
              return (
                <div key={q.id} className={`border rounded-xl p-3.5 ${hasAns ? 'bg-cream-100 border-cream-300' : 'bg-cream-50 border-dashed border-cream-400'}`} data-testid={`live-q-${i + 1}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="text-[13px] font-semibold text-espresso leading-snug">{i + 1}. {q.text}</p>
                    <span className="text-[10.5px] font-bold text-sand-500 whitespace-nowrap">{q.type === 'mc' ? 'MC' : q.type === 'mc_multi' ? 'MC mehrfach' : 'Offen'} · {q.points} P.</span>
                  </div>
                  {q.type === 'mc_multi' ? (
                    <p className="text-[12.5px] mt-1.5">
                      {hasAns ? (
                        <span className="text-sand-800 font-semibold">
                          Gewählt: {(Array.isArray(ans) ? ans : []).map((oi) => String.fromCharCode(65 + Number(oi))).join(', ')}
                          {' '}(richtig: {(q.correct_indices || []).map((oi) => String.fromCharCode(65 + Number(oi))).join(', ')})
                        </span>
                      ) : <span className="text-sand-400 italic">Noch keine Auswahl</span>}
                    </p>
                  ) : q.type === 'mc' ? (
                    <p className="text-[12.5px] mt-1.5">
                      {hasAns ? (
                        <span className={Number(ans) === Number(q.correct_index) ? 'text-green-800 font-semibold' : 'text-red-800 font-semibold'}>
                          Gewählt: {String.fromCharCode(65 + Number(ans))}) {(q.options || [])[Number(ans)]}
                          {Number(ans) === Number(q.correct_index) ? ' ✓' : ` ✗ (richtig: ${String.fromCharCode(65 + Number(q.correct_index))})`}
                        </span>
                      ) : <span className="text-sand-400 italic">Noch keine Auswahl</span>}
                    </p>
                  ) : (
                    <p className={`text-[12.5px] mt-1.5 whitespace-pre-wrap leading-relaxed ${hasAns ? 'text-sand-800' : 'text-sand-400 italic'}`}>
                      {hasAns ? String(ans) : 'Noch keine Antwort…'}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamLiveView;
